/**
 * Runtime accessor for brand-tokenised CSS vars (set by AppearanceProvider).
 *
 * For most chrome, prefer Tailwind classes (`bg-coral-500`, `text-text`,
 * `border-line`) or inline `rgb(var(--c-…))`. Use these helpers only when a
 * concrete hex/rgba string is required (lookup tables consumed by canvas,
 * mermaid, third-party libs that don't accept CSS vars).
 *
 * Token argument matches the suffix of the CSS var, e.g. `coral-500`,
 * `text-default`, `surface-muted`, `brand-gold`, `shadow-color`.
 */

const FALLBACKS: Record<string, string> = {
  'coral-50': '253 245 243',
  'coral-100': '252 232 228',
  'coral-200': '249 208 200',
  'coral-300': '245 177 164',
  'coral-400': '230 145 128',
  'coral-500': '217 127 111',
  'coral-600': '198 102 86',
  'coral-700': '165 78 65',
  'coral-800': '125 58 48',
  'coral-900': '86 40 33',
  'text-default': '47 36 27',
  'text-muted': '107 93 82',
  'text-subtle': '107 93 82',
  'text-faint': '163 150 134',
  'border-default': '232 224 213',
  'border-strong': '212 203 189',
  'border-soft': '240 235 227',
  'surface-default': '255 255 255',
  'surface-muted': '251 250 248',
  'surface-subtle': '245 241 236',
  'surface-bright': '253 252 251',
  'shadow-color': '47 36 27',
  'brand-gold': '224 182 102',
};

function readTriplet(token: string): string {
  if (typeof document === 'undefined') return FALLBACKS[token] ?? '217 127 111';
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--c-${token}`).trim();
  return v || FALLBACKS[token] || '217 127 111';
}

export function getBrandColor(token: string): string {
  return `rgb(${readTriplet(token)})`;
}

export function getBrandColorAlpha(token: string, alpha: number): string {
  return `rgb(${readTriplet(token)} / ${alpha})`;
}

/**
 * Returns the raw "R G B" triplet — useful when composing into your own
 * `rgb(...)` / `rgba(...)` strings or for libraries that prefer the
 * components separately.
 */
export function getBrandColorTriplet(token: string): string {
  return readTriplet(token);
}

/**
 * Subscribe to brand changes. AppearanceProvider toggles
 * `document.documentElement.dataset.brand`, which we observe via
 * MutationObserver. Returns an unsubscribe fn.
 *
 * Use for non-React consumers (mermaid theme rebuild, canvas redraw).
 */
export function subscribeBrandChange(cb: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  const root = document.documentElement;
  let lastBrand = root.dataset.brand;
  const obs = new MutationObserver(() => {
    if (root.dataset.brand !== lastBrand) {
      lastBrand = root.dataset.brand;
      cb();
    }
  });
  obs.observe(root, { attributes: true, attributeFilter: ['data-brand'] });
  return () => obs.disconnect();
}
